<script setup lang="ts">
import { ref, defineEmits, onMounted } from "vue";
import { setCookie, getCookie, deleteCookie } from "../utils/cookies";
import { useGeneralStore } from "../stores/general";
import GenericButton from "./ui/GenericButton.vue";
import { scrollToInput } from "../utils/common";

const general = useGeneralStore();
const key = ref("");
const errorMessage = ref("");
const loading = ref(false);
const sessionEmail = ref("");
const emits = defineEmits(["change-view"]);

async function validateKey(event: any, isNewTry = false) {
  event.preventDefault();
  event.stopPropagation();
  loading.value = true;
  if (key.value.length < 8 || key.value.length > 32) {
    loading.value = false;
    errorMessage.value = "La clave debe tener entre 8 y 32 caracteres";
    return;
  }
  const fingerprint = getCookie("fingerprint") as string;
  if (!fingerprint && !isNewTry) {
    general.generateFingerprint(() => {
      validateKey(event, true);
    });
    loading.value = false;
    return;
  } else if (!fingerprint && isNewTry) {
    errorMessage.value = "No se logró generar la huella digital";
    loading.value = false;
    return;
  }
  const days = general.getBooth.customConfig?.duracionSesion;
  setCookie("fingerprint", fingerprint, days);
  const session = await general.getSession();
  const accessToken = session.tokens?.accessToken as object;
  const res = (await general.postMedicalBoothsValidation({
    claveCabina: key.value,
    fingerprint: fingerprint,
    accessToken: accessToken,
  })) as any;
  if (res.code === 200) {
    setCookie("booth", key.value, days);
    general.setBooth(res.data);
    emits("change-view", "baseInformation");
  } else {
    deleteCookie("booth");
    deleteCookie("fingerprint");
  }
  errorMessage.value = res.message;
  loading.value = false;
}

async function handleClick() {
  if (!document || !document.exitFullscreen) {
    errorMessage.value = "No se puede salir de pantalla completa...";
    return;
  }
  await document.exitFullscreen();
}

onMounted(() => {
  general.getSession().then((session) => {
    sessionEmail.value =
      session?.tokens?.signInDetails?.loginId?.split("@")[0] || "";
  });
});
</script>

<template>
  <div class="flex items-center justify-center max-h-screen h-screen">
    <div class="absolute top-1 left-1 text-[10px]">
      <p>{{ sessionEmail }}</p>
    </div>
    <GenericButton
      variant="ghost"
      @click="handleClick"
      class="absolute top-0 right-3 text-primary-500"
    >
      Salir
    </GenericButton>
    <div
      class="flex flex-col items-center gap-4 rounded-lg shadow-lg border border-gray-300 p-4 text-center"
    >
      <h1 class="text-2xl font-bold text-primary-900">
        Ingrese clave de cabina
      </h1>
      <hr class="my-4 border-gray-200 w-full" />
      <form
        class="flex flex-col gap-4"
        @submit.prevent="(event: any) => validateKey(event)"
      >
        <input
          @focus="scrollToInput"
          type="text"
          id="key"
          name="key"
          class="w-full border border-gray-300 rounded p-2"
          placeholder="Clave de cabina"
          required
          v-model="key"
        />
        <GenericButton
          variant="primary"
          type="submit"
          :disabled="loading"
          :loading="loading"
          class="bg-gradient-to-r from-primary-400 to-primary-900 text-white px-4 py-2 rounded-full mt-4"
        >
          Validar
        </GenericButton>
      </form>
      <p v-if="errorMessage" class="text-red-500 text-sm mt-2">
        {{ errorMessage }}
      </p>
    </div>
  </div>
</template>
